import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpUtilService } from '@services/http-util.service';
import { Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  retry,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';

// ============== Interfaces ==============

export interface StandardResponse<T> {
  data: T;
  message: string;
  status: number;
}

export interface DataxColumnMaskingPermissions {
  privilege_type: 'COLUMN_MASKING';
  privileged_resource: string;
  view_access: boolean;
}

export interface DataServiceRole {
  role_name: string;
  scaffold_role_id: number;
  privileges: DataServiceRoleTablePermission;
  updated_at: string; // timestamp
  updated_by: string;
}

export function getColumnMaskingViewAccessMap(
  role: DataServiceRole | null | undefined
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!role?.privileges?.length) {
    return out;
  }
  for (const p of role.privileges) {
    if (p.privilege_type === 'COLUMN_MASKING') {
      const cm = p as DataxColumnMaskingPermissions;
      out[cm.privileged_resource] = !!cm.view_access;
    }
  }
  return out;
}

export interface DataServiceRoleCheck {
  role_name: string;
  scaffold_role_id: number;
  updated_at: string; // timestamp
  updated_by: string;
}

export interface DataServiceRoleCreate {
  role_name: string;
  scaffold_role_id: number;
  privileges: DataServiceRoleTablePermission; // Array of privileges - empty list = no privileges
}

export interface DataServiceRoleUpdate {
  role_name: string;
  scaffold_role_id: number;
  privileges: Partial<DataServiceRoleTablePermission>;
}

export type DataServiceRoleTablePermission = Array<
  | DataxTablePermissions
  | DataxSourcePermissions
  | DataxColumnMaskingPermissions
  | DataxDatabasePermissions
  | DataxSchemaPermissions
  | DataxWarehousePermissions
>;

export interface DataxTablePermissions {
  privilege_type: 'ENTITY';
  source: string; // which source/folder this entity belongs to
  privileged_resource: string; // entity name
  view_access: boolean;
}

export interface DataxSourcePermissions {
  privilege_type: 'SOURCE';
  privileged_resource: string; // source name
  view_access: boolean;
}

export interface DataxDatabasePermissions {
  privilege_type: 'DATABASE';
  view_access: boolean;
  source: string;
  entity_name: string;
}

export interface DataxSchemaPermissions {
  privilege_type: 'SCHEMA';
  view_access: boolean;
  source: string;
  entity_name: string;
}

export interface DataxWarehousePermissions {
  privilege_type: 'WAREHOUSE';
  view_access: boolean;
  source: string;
  entity_name: string;
}


export interface GetRolesQueryParams {
  privilege_type?:
    | 'SOURCE'
    | 'ENTITY'
    | 'DATABASE'
    | 'SCHEMA'
    | 'WAREHOUSE'
    | 'COLUMN_MASKING';
  privileged_resource?: string;
  role_name?: string;
  scaffold_role_id?: number;
}

/** HTTP 503 from role updates: server may apply changes asynchronously; we confirm via GET /entities/roles/check. */
const ROLE_UPDATE_POLL_INTERVAL_MS = 2000;
const ROLE_UPDATE_POLL_TIMEOUT_MS = 5 * 60 * 1000;
/** After this delay while polling, notify the user that confirmation is still in progress. */
const ROLE_UPDATE_SLOW_NOTIFY_MS = 15_000;

/** Optional hooks when PUT returns 503 and we poll `roles/check` for completion. */
export interface DeferredRoleUpdatePollCallbacks {
  /** Invoked once if polling lasts longer than {@link ROLE_UPDATE_SLOW_NOTIFY_MS}. */
  onSlow?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class DataxRoleService {
  constructor(private http: HttpUtilService) {}

  /** API may return `data: [...]` or `data: { results: [...] }`. */
  private getCheckRoleRowsFromResponse(
    resp: StandardResponse<DataServiceRoleCheck[]>
  ): DataServiceRoleCheck[] {
    const d = resp?.data as unknown;
    if (Array.isArray(d)) {
      return d as DataServiceRoleCheck[];
    }
    if (
      d &&
      typeof d === 'object' &&
      Array.isArray((d as { results?: unknown }).results)
    ) {
      return (d as { results: DataServiceRoleCheck[] }).results;
    }
    return [];
  }

  /**
   * Get data service roles with optional filtering
   * GET /entities/roles
   * @param queryParams - Optional query parameters for filtering roles
   * @returns Observable<StandardResponse<{ results: DataServiceRole[]; row_count: number }>>
   */
  getRoles$(
    queryParams?: GetRolesQueryParams
  ): Observable<
    StandardResponse<{ results: DataServiceRole[]; row_count: number }>
  > {
    const params = new URLSearchParams();
    if (queryParams?.privilege_type)
      params.set('privilege_type', queryParams.privilege_type);
    if (queryParams?.privileged_resource)
      params.set('privileged_resource', queryParams.privileged_resource);
    if (queryParams?.role_name) params.set('role_name', queryParams.role_name);
    if (queryParams?.scaffold_role_id)
      params.set('scaffold_role_id', String(queryParams.scaffold_role_id));

    const queryString = params.toString();
    const url = `/entities/roles${queryString ? `?${queryString}` : ''}`;

    return this.http.request('GET', url, null, null, true);
  }

  /**
   * Check if roles are up to date before making updates
   * GET /entities/roles/check
   * Used prior to a PUT-batch call to determine if to be made updates are using outdated details
   * @returns Observable<StandardResponse<DataServiceRoleCheck[]>>
   */
  checkRoles$(): Observable<StandardResponse<DataServiceRoleCheck[]>> {
    return this.http.request('GET', `entities/roles/check`, null, null, true);
  }

  /**
   * Create a data service role
   * POST /entities/roles
   * @param role - New role to create
   * @returns Observable<StandardResponse<DataServiceRole>>
   */
  createRole$(
    role: DataServiceRoleCreate
  ): Observable<StandardResponse<DataServiceRole>> {
    // TODO: Replace with real API call when backend is ready

    return this.http.request('POST', `entities/roles`, role, null, true);
  }

  /**
   * After HTTP 503 on PUT, poll GET /entities/roles/check until each role shows an `updated_at`
   * strictly after the baseline for that role.
   *
   * Prefer `priorUpdatedAtByRole` (values from before the PUT) so we still detect completion if
   * the server applies the update before our first snapshot. Otherwise baseline is taken from the
   * first check response after 503.
   */
  private confirmRolesUpdatedViaCheckPoll$(
    roles: DataServiceRoleUpdate[],
    priorUpdatedAtByRole?: Record<string, string | null | undefined>,
    deferredPoll?: DeferredRoleUpdatePollCallbacks
  ): Observable<StandardResponse<DataServiceRole[]>> {
    const roleNames = [...new Set(roles.map(r => r.role_name))];

    return this.checkRoles$().pipe(
      switchMap(firstCheck => {
        const rows = this.getCheckRoleRowsFromResponse(firstCheck);
        const baselineByName = new Map<string, string | null>();
        for (const name of roleNames) {
          const prior = priorUpdatedAtByRole?.[name];
          if (typeof prior === 'string' && prior.length > 0) {
            baselineByName.set(name, prior);
          } else {
            const row = rows.find(r => r.role_name === name);
            baselineByName.set(name, row?.updated_at ?? null);
          }
        }

        let slowNotifyTimerId: ReturnType<typeof setTimeout> | undefined;
        if (deferredPoll?.onSlow) {
          slowNotifyTimerId = setTimeout(() => {
            slowNotifyTimerId = undefined;
            deferredPoll.onSlow?.();
          }, ROLE_UPDATE_SLOW_NOTIFY_MS);
        }

        return timer(0, ROLE_UPDATE_POLL_INTERVAL_MS).pipe(
          switchMap(() => this.checkRoles$()),
          map(checkResponse =>
            this.getCheckRoleRowsFromResponse(checkResponse)
          ),
          filter(checkRows => {
            return roleNames.every(name => {
              const row = checkRows.find(r => r.role_name === name);
              const u = row?.updated_at;
              if (!u) {
                return false;
              }
              const baseline = baselineByName.get(name);
              if (baseline === null || baseline === undefined) {
                return true;
              }
              if (baseline === '') {
                return true;
              }
              return new Date(u) > new Date(baseline);
            });
          }),
          take(1),
          timeout(ROLE_UPDATE_POLL_TIMEOUT_MS),
          finalize(() => {
            if (slowNotifyTimerId !== undefined) {
              clearTimeout(slowNotifyTimerId);
            }
          }),
          map(
            (checkRows: DataServiceRoleCheck[]) =>
              ({
                data: checkRows as unknown as DataServiceRole[],
                message: 'Roles updated',
                status: 200,
              }) as StandardResponse<DataServiceRole[]>
          )
        );
      })
    );
  }

  /**
   * Update data service roles
   * PUT /entities/roles
   * @param roles - List of updated roles (only provide the updated roles)
   * @returns Observable<StandardResponse<DataServiceRole[]>>
   *
   * HTTP 503 (service unavailable) is treated as a deferred update: we poll GET /entities/roles/check
   * until each role shows a newer `updated_at` than the baseline (see `options.priorUpdatedAtByRole`
   * or the first check response after 503). Pass `skipDeferredConfirmation: true` to treat 503 as
   * success immediately (e.g. best-effort seeding that must not block navigation).
   *
   * @param options.priorUpdatedAtByRole — map of `role_name` → `updated_at` the client had before
   * this PUT; recommended for RBAC and other flows where the save snapshot is available.
   */
  updateRoles$(
    roles: DataServiceRoleUpdate[],
    options?: {
      priorUpdatedAtByRole?: Record<string, string | null | undefined>;
      deferredPoll?: DeferredRoleUpdatePollCallbacks;
      skipDeferredConfirmation?: boolean;
    }
  ): Observable<StandardResponse<DataServiceRole[]>> {
    return this.http.request('PUT', `entities/roles`, roles, null, true).pipe(
      catchError((err: unknown) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 503 &&
          roles?.length
        ) {
          if (options?.skipDeferredConfirmation) {
            return of({
              data: [] as DataServiceRole[],
              message: 'Role update deferred',
              status: 503,
            } as StandardResponse<DataServiceRole[]>);
          }
          return this.confirmRolesUpdatedViaCheckPoll$(
            roles,
            options?.priorUpdatedAtByRole,
            options?.deferredPoll
          );
        }
        return throwError(() => err);
      })
    );
  }

  getRolesWithAccessForSource$(
    source: string
  ): Observable<
    Array<{
      role_name: string;
      scaffold_role_id: number;
      view_access: boolean;
    }>
  > {
    return this.getRoles$({
      privilege_type: 'SOURCE',
      privileged_resource: source,
    }).pipe(
      map(response => {
        if (!Array.isArray(response?.data?.results)) {
          return [];
        }
        return response.data.results.map(role => {
          const sourcePermission = role.privileges.find(
            privilege =>
              privilege.privilege_type === 'SOURCE' &&
              (privilege as DataxSourcePermissions).privileged_resource ===
                source
          ) as DataxSourcePermissions | undefined;

          return {
            role_name: role.role_name,
            scaffold_role_id: role.scaffold_role_id,
            view_access: sourcePermission?.view_access || false,
          };
        });
      })
    );
  }

  /**
   * Best-effort seed of ENTITY privileges from SOURCE defaults.
   * Transient GET 503s are retried so the seeding PUT is still issued when the
   * roles service recovers. Non-503 GET failures propagate. PUT uses
   * `skipDeferredConfirmation` — the update request is still sent; we just
   * do not wait on 503 deferred confirmation (so callers are not blocked).
   */
  setInitialEntityWithSource$(entityName: string, source: string) {
    const newPermission: DataxTablePermissions = {
      privilege_type: 'ENTITY',
      source,
      privileged_resource: entityName,
      view_access: false,
    };

    return this.getRoles$({
      privilege_type: 'SOURCE',
      privileged_resource: source,
    }).pipe(
      // GET 503 means roles are temporarily unavailable; retry so we can still
      // build and PUT ENTITY privileges instead of navigating with no seed.
      retry({
        count: 3,
        delay: (error, retryCount) => {
          if (
            !(error instanceof HttpErrorResponse) ||
            error.status !== 503
          ) {
            throw error;
          }
          return timer(1000 * retryCount);
        },
      }),
      switchMap(response => {
        if (!Array.isArray(response?.data?.results)) return of(null);
        const rolesToUpdate: DataServiceRoleUpdate[] =
          response.data.results.reduce((acc, role) => {
            const sourcePermission = role.privileges.find(
              privilege =>
                privilege.privilege_type === 'SOURCE' &&
                privilege.privileged_resource === source
            ) as DataxSourcePermissions | undefined;

            acc.push({
              role_name: role.role_name,
              scaffold_role_id: role.scaffold_role_id,
              privileges: [
                {
                  ...newPermission,
                  view_access: sourcePermission?.view_access || false,
                },
              ],
            });
            return acc;
          }, [] as DataServiceRoleUpdate[]);

        // PUT may return 503 while the server applies asynchronously; do not poll
        // here — callers must not be blocked on deferred confirmation.
        return this.updateRoles$(rolesToUpdate, {
          skipDeferredConfirmation: true,
        });
      })
    );
  }
}
